import { ApplyFilter } from "@coworker/apprestructured/src/shared/filters/types/apply";
import {
  FilterType,
  FilterValue,
  InputType,
  MultiSelectFilter,
} from "@coworker/apprestructured/src/shared/filters/types/filter";
import { useTranslation } from "@coworker/locales";
import { Article } from "../types/article";
import { Media } from "../types/media";

const PRODUCT_ATTRIBUTES_ID = "productAttributes";
const PRODUCT_ATTRIBUTE_ITEM_TAGS = [
  "NEW_LOWER_PRICE",
  "NEW_PRODUCT",
  "FAMILY_PRICE",
] as const;
const PRODUCT_ATTRIBUTE_4AK1_BADGES = [
  "BREATHTAKING_ITEM_ACE",
  "SUSTAINABLE_ACE",
  "TURNOVER_ACE",
  "PROFIT_ACE",
  "KING",
] as const;

type ProductAttributeFilterArticle = Article & {
  aggregatedArticles?: Article[];
  combinationArticles?: Article[];
};

export function useProductAttributesMultiSelectArticleFilter(
  media?: Media
): ApplyFilter<MultiSelectFilter, Article> {
  const { t } = useTranslation();
  const defaultValue = { filterId: PRODUCT_ATTRIBUTES_ID, value: [] };

  return {
    filter: {
      inputType: InputType.MultiSelect,
      filterType: FilterType.Filter,
      id: PRODUCT_ATTRIBUTES_ID,
      title: t("ProductAttributesString"),
      options: [
        { value: [], title: t("allArticlesString") },
        { value: "NEW_LOWER_PRICE", title: t("NewLowerPriceString") },
        { value: "NEW_PRODUCT", title: t("NewArticleString") },
        { value: "TURNOVER_ACE", title: t("TurnoverString") },
        { value: "PROFIT_ACE", title: t("ProfitString") },
        { value: "BREATHTAKING_ITEM_ACE", title: t("BTIString") },
        { value: "SUSTAINABLE_ACE", title: t("SustainabilityString") },
        { value: "KING", title: t("KingString") },
        { value: "FAMILY_PRICE", title: t("FamilyPriceOfferString") },
        { value: "MAIN_PRODUCTS", title: t("MainProductsString") },
      ],
      enabled: true,
      defaultValue,
    },
    filterCompare: (
      item: ProductAttributeFilterArticle,
      { value }: FilterValue
    ) => {
      if (!Array.isArray(value) || value.length === 0) {
        return true;
      }

      const selectedItemTags = value.filter((selectedValue) =>
        PRODUCT_ATTRIBUTE_ITEM_TAGS.includes(
          selectedValue as (typeof PRODUCT_ATTRIBUTE_ITEM_TAGS)[number]
        )
      );
      const selected4AK1Badges = value.filter((selectedValue) =>
        PRODUCT_ATTRIBUTE_4AK1_BADGES.includes(
          selectedValue as (typeof PRODUCT_ATTRIBUTE_4AK1_BADGES)[number]
        )
      );
      const shouldFilterMainProducts = value.includes("MAIN_PRODUCTS");

      if (
        selectedItemTags.length === 0 &&
        selected4AK1Badges.length === 0 &&
        !shouldFilterMainProducts
      ) {
        return true;
      }

      const articlesToMatch = [item, ...(item.combinationArticles ?? [])];
      const hasSelectedItemTag = articlesToMatch.some((article) =>
        selectedItemTags.some((tag) => article.itemTags?.includes(tag))
      );
      const hasSelected4AK1Badge = articlesToMatch.some((article) =>
        selected4AK1Badges.some((badge) => article.badges4AK1?.includes(badge))
      );

      const articleIds = [
        item.id,
        ...(item.aggregatedArticles?.map((article) => article.id) ?? []),
      ];
      const isMainProduct =
        shouldFilterMainProducts &&
        articleIds.some(
          (articleId) =>
            articleId === media?.mainProductId1 ||
            articleId === media?.mainProductId2
        );

      return hasSelectedItemTag || hasSelected4AK1Badge || isMainProduct;
    },
  };
}
